import React, { useState, useMemo } from "react";
import "./CrossoverDashboard.css";

const formatTime = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return "—";
  }
};

const formatPrice = (p) => {
  if (p == null) return "—";
  return `₹${Number(p).toLocaleString("en-IN", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
};

const getAlignment = (value, direction) => {
  const s = String(value || "").toLowerCase();
  let v = 0;
  if (["buy", "bullish", "accumulation"].some((k) => s.includes(k))) v = 1;
  if (["sell", "bearish", "distribution"].some((k) => s.includes(k))) v = -1;
  const aligned = v === direction;
  const opposed = v === -direction;
  return {
    icon: aligned ? "✓" : opposed ? "✗" : "—",
    cls: aligned
      ? "cd-ind--pass"
      : opposed
        ? "cd-ind--fail"
        : "cd-ind--neutral",
    label: v === 1 ? "Bullish" : v === -1 ? "Bearish" : "Neutral",
  };
};

const INDICATOR_NAMES = {
  vwap: "VWAP",
  rsiMacd: "RSI + MACD",
  utBot: "UT Bot",
  bb: "BB + Awesome",
  smartMoney: "Smart Money",
};

const GATE_EXPLANATIONS = {
  TIME_WINDOW: "Outside allowed windows (9:45-11:30 or 1:00-2:45)",
  HISTOGRAM_WEAK: "Histogram < 0.5 — weak momentum",
  HISTOGRAM_REVERSED: "Histogram reversed — momentum fading",
  ATR_LOW: "ATR below 6 — low volatility",
  VWAP_OPPOSING: "Price on wrong side of VWAP",
  SMART_MONEY_OPPOSING: "Smart money opposes signal",
  CONFLUENCE_0_OF_5: "0/5 indicators aligned",
  CONFLUENCE_1_OF_5: "Only 1/5 indicators aligned",
  CONFLUENCE_2_OF_5: "Only 2/5 indicators aligned (need ≥3)",
};

// ─── Sub Components ───

const Badge = ({ type, children }) => (
  <span className={`cd-badge cd-badge--${type}`}>{children}</span>
);

const StatCard = ({ icon, label, value, valueClass, sub }) => (
  <div className="cd-stat">
    <div className="cd-stat__label">
      {icon && <span className="cd-stat__icon">{icon}</span>}
      {label}
    </div>
    <div className={`cd-stat__value ${valueClass || ""}`}>{value}</div>
    {sub && <div className="cd-stat__sub">{sub}</div>}
  </div>
);

const ScoreBar = ({ score, max = 5 }) => {
  const pct = (score / max) * 100;
  const level = score >= 4 ? "high" : score >= 3 ? "mid" : "low";
  return (
    <div className="cd-score">
      <div className="cd-score__track">
        <div
          className={`cd-score__fill cd-score__fill--${level}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`cd-score__label cd-score__label--${level}`}>
        {score}/{max}
      </span>
    </div>
  );
};

// ─── Signal Card ───

const SignalCard = ({ signal, isExpanded, onToggle }) => {
  const direction = signal.type === "BUY" ? 1 : -1;
  const indicators =
    signal.indicators_snapshot || signal.confluence_detail || {};

  return (
    <div
      className={`cd-signal ${isExpanded ? "cd-signal--expanded" : ""}`}
      onClick={onToggle}
    >
      <div className="cd-signal__row">
        <div
          className={`cd-signal__bar ${signal.passed ? "cd-signal__bar--pass" : "cd-signal__bar--fail"}`}
        />
        <div className="cd-signal__time">{formatTime(signal.timestamp)}</div>
        <Badge type={signal.type === "BUY" ? "buy" : "sell"}>
          {signal.type}
        </Badge>
        <Badge type={signal.passed ? "passed" : "rejected"}>
          {signal.passed ? "PASSED" : "REJECTED"}
        </Badge>
        <div className="cd-signal__price">{formatPrice(signal.price)}</div>
        <div className="cd-signal__score-wrap">
          <ScoreBar score={signal.confluence_score || 0} />
        </div>
        <span
          className={`cd-signal__chevron ${isExpanded ? "cd-signal__chevron--open" : ""}`}
        >
          ▾
        </span>
      </div>

      {isExpanded && (
        <div className="cd-signal__detail">
          <div className="cd-signal__detail-grid">
            {/* MACD Values */}
            <div>
              <div className="cd-section-title">MACD values</div>
              <div className="cd-macd-grid">
                <div className="cd-macd-item">
                  <span className="cd-macd-item__label">MACD</span>
                  <span className="cd-macd-item__value">
                    {signal.macd?.toFixed(2) || "—"}
                  </span>
                </div>
                <div className="cd-macd-item">
                  <span className="cd-macd-item__label">Signal</span>
                  <span className="cd-macd-item__value">
                    {signal.signal_line?.toFixed(2) || "—"}
                  </span>
                </div>
                <div className="cd-macd-item">
                  <span className="cd-macd-item__label">Histogram</span>
                  <span className="cd-macd-item__value">
                    {signal.histogram?.toFixed(2) || "—"}
                  </span>
                </div>
              </div>
              <div className="cd-macd-note">
                {signal.type === "BUY"
                  ? "MACD crossed above signal line → bullish momentum"
                  : "MACD crossed below signal line → bearish momentum"}
              </div>
            </div>

            {/* Confluence */}
            <div>
              <div className="cd-section-title">
                Confluence ({signal.confluence_score || 0}/5 aligned)
              </div>
              {Object.keys(indicators).length > 0 ? (
                <div className="cd-indicators">
                  {Object.entries(indicators).map(([key, val]) => {
                    const r = getAlignment(val, direction);
                    return (
                      <div key={key} className="cd-ind-row">
                        <span className={`cd-ind-row__icon ${r.cls}`}>
                          {r.icon}
                        </span>
                        <span className="cd-ind-row__name">
                          {INDICATOR_NAMES[key] || key}
                        </span>
                        <span className={`cd-ind-row__val ${r.cls}`}>
                          {r.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="cd-text--dim">No indicator data</p>
              )}
            </div>
          </div>

          {signal.rejected_by && (
            <div className="cd-reject-box">
              <div className="cd-reject-box__label">
                ✗ Rejected: {signal.rejected_by.replace(/_/g, " ")}
              </div>
              {GATE_EXPLANATIONS[signal.rejected_by] && (
                <div className="cd-reject-box__reason">
                  {GATE_EXPLANATIONS[signal.rejected_by]}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Trade Card ───

const TradeCard = ({ trade }) => {
  const isOpen = trade.status === "OPEN";
  const pnlPts = Number(trade.pnl_points) || 0;
  const pnlAmt = Number(trade.pnl_amount) || 0;
  const isBuy = trade.direction === "BUY";

  const getPnlCalc = () => {
    if (trade.status !== "CLOSED" || !trade.exit_price) return null;
    if (isBuy) {
      return `(${formatPrice(trade.exit_price)} − ${formatPrice(trade.entry_price)}) × ${trade.quantity} = ₹${pnlAmt > 0 ? "+" : ""}${pnlAmt.toLocaleString("en-IN")}`;
    }
    return `(${formatPrice(trade.entry_price)} − ${formatPrice(trade.exit_price)}) × ${trade.quantity} = ₹${pnlAmt > 0 ? "+" : ""}${pnlAmt.toLocaleString("en-IN")}`;
  };

  const exitBadge =
    trade.exit_reason === "STOP_LOSS"
      ? "sl"
      : trade.exit_reason === "TARGET_HIT"
        ? "target"
        : trade.exit_reason === "TRAIL_STOP"
          ? "trail"
          : "closed";

  return (
    <div className={`cd-trade ${isOpen ? "cd-trade--open" : ""}`}>
      <div className="cd-trade__header">
        <span
          className={`cd-trade__dot ${isOpen ? "cd-trade__dot--open" : ""}`}
        />
        <span className="cd-trade__symbol">{trade.symbol}</span>
        <Badge type={isBuy ? "buy" : "sell"}>{trade.direction}</Badge>
        <Badge type={isOpen ? "open" : "closed"}>{trade.status}</Badge>
        {trade.exit_reason && (
          <Badge type={exitBadge}>{trade.exit_reason.replace(/_/g, " ")}</Badge>
        )}
        <span className="cd-trade__mode">{trade.mode || "paper"}</span>
        <span className="cd-trade__time">
          {formatTime(trade.entry_time)}
          {trade.exit_time ? ` → ${formatTime(trade.exit_time)}` : ""}
        </span>
      </div>

      <div className="cd-trade__grid">
        <div className="cd-field">
          <span className="cd-field__label">Entry</span>
          <span className="cd-field__value">
            {formatPrice(trade.entry_price)}
          </span>
        </div>
        <div className="cd-field">
          <span className="cd-field__label">Qty</span>
          <span className="cd-field__value">{trade.quantity}</span>
        </div>
        <div className="cd-field">
          <span className="cd-field__label">Stop loss</span>
          <span className="cd-field__value cd-text--red">
            {formatPrice(trade.stop_loss)}
          </span>
        </div>
        <div className="cd-field">
          <span className="cd-field__label">Target</span>
          <span className="cd-field__value cd-text--green">
            {formatPrice(trade.target)}
          </span>
        </div>
        {trade.trailing_sl && (
          <div className="cd-field">
            <span className="cd-field__label">Trail SL</span>
            <span className="cd-field__value cd-text--amber">
              {formatPrice(trade.trailing_sl)}
            </span>
          </div>
        )}
        {trade.exit_price && (
          <div className="cd-field">
            <span className="cd-field__label">Exit</span>
            <span className="cd-field__value">
              {formatPrice(trade.exit_price)}
            </span>
          </div>
        )}
        {trade.status === "CLOSED" && (
          <>
            <div className="cd-field">
              <span className="cd-field__label">P&L points</span>
              <span
                className={`cd-field__value ${pnlPts > 0 ? "cd-text--green" : pnlPts < 0 ? "cd-text--red" : ""}`}
              >
                {pnlPts > 0 ? "+" : ""}
                {pnlPts}
              </span>
            </div>
            <div className="cd-field">
              <span className="cd-field__label">P&L amount</span>
              <span
                className={`cd-field__value ${pnlAmt > 0 ? "cd-text--green" : pnlAmt < 0 ? "cd-text--red" : ""}`}
              >
                {pnlAmt > 0 ? "+" : ""}₹{pnlAmt.toLocaleString("en-IN")}
              </span>
            </div>
          </>
        )}
        <div className="cd-field">
          <span className="cd-field__label">Score</span>
          <span className="cd-field__value">
            {trade.confluence_score || "—"}/5
          </span>
        </div>
      </div>

      {trade.status === "CLOSED" && (
        <div className="cd-trade__pnl-section">
          <div className="cd-trade__pnl-track">
            <div
              className={`cd-trade__pnl-fill ${pnlPts > 0 ? "cd-trade__pnl-fill--profit" : "cd-trade__pnl-fill--loss"}`}
              style={{ width: `${Math.min(Math.abs(pnlPts) * 2, 100)}%` }}
            />
          </div>
          <span className="cd-trade__pnl-calc">{getPnlCalc()}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───

const CrossoverDashboard = ({ crossoverSignals, tradesToday, symbol }) => {
  const [tab, setTab] = useState("signals");
  const [expandedSignal, setExpandedSignal] = useState(null);

  const stats = crossoverSignals?.stats || {
    total: 0,
    passed: 0,
    rejected: 0,
    gates: {},
  };
  const allSignals = crossoverSignals?.all_signals || [];
  const trades = tradesToday || [];

  const totalPnl = useMemo(
    () =>
      trades
        .filter((t) => t.pnl_amount != null)
        .reduce((sum, t) => sum + Number(t.pnl_amount), 0),
    [trades],
  );

  const openTrades = trades.filter((t) => t.status === "OPEN");
  const closedTrades = trades.filter((t) => t.status === "CLOSED");
  const winTrades = closedTrades.filter((t) => Number(t.pnl_points) > 0);
  const lossTrades = closedTrades.filter((t) => Number(t.pnl_points) < 0);
  const winRate =
    closedTrades.length > 0
      ? Math.round((winTrades.length / closedTrades.length) * 100)
      : null;

  const gateEntries = Object.entries(stats.gates || {}).sort(
    (a, b) => b[1] - a[1],
  );

  return (
    <div className="cd-dashboard">
      {/* Header */}
      <div className="cd-header">
        <div className="cd-header__row">
          <h3 className="cd-header__title">Signal & trade dashboard</h3>
          {symbol && <span className="cd-header__symbol">{symbol}</span>}
        </div>
        <p className="cd-header__sub">
          MACD crossover → 5-gate filter → auto paper trade execution
        </p>
      </div>

      {/* Stats */}
      <div className="cd-stats">
        <StatCard
          icon="📡"
          label="Signals"
          value={stats.total}
          sub={`${stats.passed} passed · ${stats.rejected} rejected`}
        />
        <StatCard
          icon="📊"
          label="Trades"
          value={trades.length}
          sub={`${openTrades.length} open · ${closedTrades.length} closed`}
        />
        <StatCard
          icon="💰"
          label="Day P&L"
          value={`₹${totalPnl.toLocaleString("en-IN")}`}
          valueClass={
            totalPnl > 0 ? "cd-text--green" : totalPnl < 0 ? "cd-text--red" : ""
          }
          sub={
            winRate !== null
              ? `${winTrades.length}W ${lossTrades.length}L · ${winRate}% win rate`
              : "No closed trades yet"
          }
        />
      </div>

      {/* Gate Breakdown */}
      {gateEntries.length > 0 && (
        <div className="cd-gates">
          <div className="cd-gates__title">Why signals were rejected</div>
          <div className="cd-gates__tags">
            {gateEntries.map(([gate, count]) => (
              <div
                key={gate}
                className="cd-gate-tag"
                title={GATE_EXPLANATIONS[gate] || ""}
              >
                <span className="cd-gate-tag__name">
                  {gate.replace(/_/g, " ")}
                </span>
                <span className="cd-gate-tag__count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="cd-tabs">
        {[
          { id: "signals", label: "Signals", count: allSignals.length },
          { id: "trades", label: "Trades", count: trades.length },
        ].map((t) => (
          <button
            key={t.id}
            className={`cd-tab ${tab === t.id ? "cd-tab--active" : ""}`}
            onClick={() => {
              setTab(t.id);
              setExpandedSignal(null);
            }}
          >
            {t.label}
            <span className="cd-tab__count">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Signals */}
      {tab === "signals" && (
        <div className="cd-panel">
          {allSignals.length === 0 ? (
            <div className="cd-empty">
              <div className="cd-empty__icon">📡</div>
              <div className="cd-empty__title">No signals today</div>
              <div className="cd-empty__sub">
                Signals appear during market hours when MACD crossovers fire on
                1-min candles
              </div>
            </div>
          ) : (
            allSignals.map((signal, idx) => (
              <SignalCard
                key={signal.id || idx}
                signal={signal}
                isExpanded={expandedSignal === idx}
                onToggle={() =>
                  setExpandedSignal(expandedSignal === idx ? null : idx)
                }
              />
            ))
          )}
        </div>
      )}

      {/* Trades */}
      {tab === "trades" && (
        <div className="cd-panel">
          {trades.length === 0 ? (
            <div className="cd-empty">
              <div className="cd-empty__icon">📊</div>
              <div className="cd-empty__title">No trades today</div>
              <div className="cd-empty__sub">
                Trades auto-execute when signals pass all 5 gates. Max 3 open
                trades at a time.
              </div>
            </div>
          ) : (
            trades.map((trade, idx) => (
              <TradeCard key={trade.id || idx} trade={trade} />
            ))
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="cd-note">
        <div className="cd-note__title">How this works</div>
        <div className="cd-note__steps">
          <div className="cd-note__step">
            <span className="cd-note__num">1</span>
            <div>
              <b>Detect</b> — Every 3 min, fetch 1-min candles and check if MACD
              crossed signal line. Bullish cross = BUY, bearish cross = SELL.
            </div>
          </div>
          <div className="cd-note__step">
            <span className="cd-note__num">2</span>
            <div>
              <b>Filter</b> — 5 gates: time window, histogram strength,
              histogram direction, ATR volatility, and confluence (≥3/5
              indicators including VWAP).
            </div>
          </div>
          <div className="cd-note__step">
            <span className="cd-note__num">3</span>
            <div>
              <b>Execute</b> — Passed signals auto-create paper trade. Quantity
              = 1% capital risk ÷ SL distance. SL = ±25 pts, Target = ±50 pts.
            </div>
          </div>
          <div className="cd-note__step">
            <span className="cd-note__num">4</span>
            <div>
              <b>Monitor</b> — Each cycle checks: SL hit → exit loss. Target hit
              → exit profit. Trail stop (after +25 pts, trails by 15 pts).
              Auto-exit 3:15 PM.
            </div>
          </div>
          <div className="cd-note__step">
            <span className="cd-note__num">5</span>
            <div>
              <b>P&L</b> — BUY: (exit − entry) × qty. SELL: (entry − exit) ×
              qty. Green = profit, red = loss.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossoverDashboard;
